import { apiClient } from './client';
import type { ApiEnvelope } from './templates';
import type { DeficiencyEntry, EntrySeverity, AppliedFinding } from './deficiency-entries';

export interface StarterEntry {
  id: string;
  title: string;
  description: string | null;
  severity: EntrySeverity;
  sectionName: string;
  componentName: string | null;
  recommendation: string | null;
  tradeTag: string | null;
  orderIndex: number;
  sourceVersion: number;
  createdAt: string;
}

export interface SuggestionRow {
  kind: 'owned' | 'starter';
  id: string;
  title: string;
  description: string | null;
  severity: EntrySeverity;
  sectionName: string;
  componentName: string | null;
  recommendation: string | null;
  tradeTag: string | null;
  useCount: number | null;
}

async function unwrap<T>(p: Promise<ApiEnvelope<T>>): Promise<T> {
  const res = await p;
  if (!res.success) throw new Error(res.error ?? 'Request failed');
  return res.data;
}

export interface ListStartersFilter {
  sectionName?: string;
  componentName?: string;
  /**
   * `'section'` restricts suggestions to rows with no component, so the
   * section-level strip doesn't duplicate chips that already live inside
   * expanded components. Only meaningful for the `/suggestions` endpoint.
   */
  scope?: 'section';
  /**
   * Dedup scope — when all three are present the server drops rows whose
   * title already has an included finding in the same (inspection, room,
   * component) bucket. componentId is optional; omit it for section-level
   * strips (dedups against the room's component_id IS NULL bucket).
   */
  inspectionId?: string;
  roomId?: string;
  componentId?: string;
}

function buildQuery(filter: ListStartersFilter): string {
  const params = new URLSearchParams();
  if (filter.sectionName) params.set('sectionName', filter.sectionName);
  if (filter.componentName) params.set('componentName', filter.componentName);
  if (filter.scope) params.set('scope', filter.scope);
  if (filter.inspectionId) params.set('inspectionId', filter.inspectionId);
  if (filter.roomId) params.set('roomId', filter.roomId);
  if (filter.componentId) params.set('componentId', filter.componentId);
  const q = params.toString();
  return q ? `?${q}` : '';
}

export interface ApplyStarterResult {
  entry: DeficiencyEntry;
  finding: AppliedFinding;
}

// Note: apiClient response interceptor auto-transforms snake_case keys to
// camelCase, so these types describe the post-transform shape.
export const libraryStartersApi = {
  listStarters: (filter: ListStartersFilter = {}) =>
    unwrap(
      apiClient.get<ApiEnvelope<StarterEntry[]>>(
        `/library/starters${buildQuery(filter)}`,
      ),
    ),

  listSuggestions: (filter: ListStartersFilter = {}) =>
    unwrap(
      apiClient.get<ApiEnvelope<SuggestionRow[]>>(
        `/library/suggestions${buildQuery(filter)}`,
      ),
    ),

  clone: (starterId: string) =>
    unwrap(
      apiClient.post<ApiEnvelope<DeficiencyEntry>>(
        `/library/starters/${starterId}/clone`,
        {},
      ),
    ),

  apply: (
    starterId: string,
    inspectionId: string,
    roomId: string,
    componentId?: string | null,
  ) =>
    unwrap(
      apiClient.post<ApiEnvelope<ApplyStarterResult>>(
        `/library/starters/${starterId}/apply`,
        {
          inspection_id: inspectionId,
          room_id: roomId,
          ...(componentId ? { component_id: componentId } : {}),
        },
      ),
    ),

  getIntro: () =>
    unwrap(
      apiClient.get<ApiEnvelope<{ dismissed: boolean }>>('/library/intro'),
    ),

  dismissIntro: () =>
    unwrap(
      apiClient.post<ApiEnvelope<{ dismissed: boolean }>>(
        '/library/intro/dismiss',
        {},
      ),
    ),
};
